import type { CharacterCustomization } from '@shared/game/types';
import type { ClientMessage, ServerMessage } from '@shared/protocol';

type RoomJoinedMessage = Extract<ServerMessage, { type: 'room_joined' }>;

export interface JoinRoomModalHandle {
  readonly element: HTMLElement;
  dispose(): void;
}

export interface JoinRoomModalOptions {
  readonly mount: HTMLElement;
  readonly send: (msg: ClientMessage) => void;
  readonly subscribe: (handler: (msg: ServerMessage) => void) => () => void;
  readonly playerName: string;
  readonly mode?: 'board_game' | 'minigames_only';
  readonly preferredCustomization?: CharacterCustomization;
  readonly initialCode?: string;
  readonly onAccepted: (message: RoomJoinedMessage) => void;
  readonly onCancel?: () => void;
}

const FONT_HEADLINE =
  "var(--typography-family-headline, 'Fredoka One', 'Baloo 2', 'Arial Black', sans-serif)";
const FONT_BODY =
  "var(--typography-family-body, 'Nunito Sans', 'Nunito', 'Arial Rounded MT Bold', sans-serif)";

function formatJoinError(text: string): string {
  switch (text) {
    case 'room_not_found':
      return 'Room not found.';
    case 'room_full':
      return 'Room is full.';
    case 'game_already_started':
      return 'That game already started.';
    case 'game_finished':
      return 'That game already finished.';
    case 'invalid_player_name':
      return 'Set a valid nick first.';
    case 'maintenance_update_pending':
      return 'Update queued. New rooms are temporarily disabled.';
    default:
      return text.replace(/_/gu, ' ');
  }
}

export function openJoinRoomModal(opts: JoinRoomModalOptions): JoinRoomModalHandle {
  let disposed = false;
  let pending = false;

  const overlay = document.createElement('div');
  overlay.dataset.testid = 'join-room-modal';
  overlay.style.cssText = [
    'position: fixed',
    'inset: 0',
    'z-index: 620',
    'display: grid',
    'place-items: center',
    'padding: 18px',
    'box-sizing: border-box',
    'background: rgba(5, 8, 14, 0.58)',
    'backdrop-filter: blur(6px)',
    'pointer-events: auto',
  ].join('; ');

  const panel = document.createElement('form');
  panel.style.cssText = [
    'width: min(390px, calc(100vw - 36px))',
    'display: flex',
    'flex-direction: column',
    'gap: 14px',
    'padding: 18px',
    'box-sizing: border-box',
    'border-radius: 8px',
    'background: rgba(20,24,34,0.96)',
    'border: 1px solid rgba(255,255,255,0.18)',
    'box-shadow: 0 18px 42px rgba(0,0,0,0.45)',
  ].join('; ');
  overlay.appendChild(panel);

  const title = document.createElement('h2');
  title.textContent = 'JOIN GAME';
  title.style.cssText = [
    `font-family: ${FONT_HEADLINE}`,
    'font-size: 24px',
    'font-weight: 800',
    'line-height: 1',
    'letter-spacing: 0',
    'margin: 0',
    'color: #FFFFFF',
    'text-align: center',
    'text-shadow: 0 2px 0 #000000',
  ].join('; ');
  panel.appendChild(title);

  const input = document.createElement('input');
  input.type = 'text';
  input.placeholder = 'ROOM CODE';
  input.maxLength = 8;
  input.autocapitalize = 'characters';
  input.spellcheck = false;
  input.value = opts.initialCode ?? '';
  input.style.cssText = [
    'height: 46px',
    'box-sizing: border-box',
    'border-radius: 8px',
    'border: 2px solid rgba(255,255,255,0.22)',
    'background: rgba(0,0,0,0.32)',
    'color: #FFFFFF',
    `font-family: ${FONT_HEADLINE}`,
    'font-size: 22px',
    'font-weight: 800',
    'letter-spacing: 2px',
    'padding: 0 12px',
    'outline: none',
    'text-align: center',
    'text-transform: uppercase',
  ].join('; ');
  panel.appendChild(input);

  const status = document.createElement('div');
  status.dataset.testid = 'join-room-modal-status';
  status.style.cssText = [
    'min-height: 17px',
    `font-family: ${FONT_BODY}`,
    'font-size: 13px',
    'font-weight: 900',
    'line-height: 1.15',
    'text-align: center',
    'color: #B0B8C8',
  ].join('; ');
  panel.appendChild(status);

  const actions = document.createElement('div');
  actions.style.cssText = ['display: grid', 'grid-template-columns: 1fr 1fr', 'gap: 10px'].join(
    '; ',
  );
  panel.appendChild(actions);

  function makeButton(label: string, tone: 'primary' | 'ghost'): HTMLButtonElement {
    const button = document.createElement('button');
    button.type = label === 'JOIN' ? 'submit' : 'button';
    button.textContent = label;
    button.style.cssText = [
      'height: 40px',
      'border-radius: 8px',
      tone === 'primary'
        ? 'background: linear-gradient(to bottom, #F0B838, #C4841C)'
        : 'background: rgba(255,255,255,0.08)',
      tone === 'primary' ? 'border: 1px solid #8A5A10' : 'border: 1px solid rgba(255,255,255,0.18)',
      'color: #FFFFFF',
      `font-family: ${FONT_HEADLINE}`,
      'font-size: 13px',
      'font-weight: 800',
      'letter-spacing: 0',
      tone === 'primary' ? 'text-shadow: 0 1px 0 #000000, 0 2px 6px rgba(0,0,0,0.45)' : '',
      'cursor: pointer',
    ].join('; ');
    return button;
  }

  const joinButton = makeButton('JOIN', 'primary');
  const cancelButton = makeButton('CANCEL', 'ghost');
  actions.append(joinButton, cancelButton);

  function setPending(nextPending: boolean): void {
    pending = nextPending;
    joinButton.disabled = pending;
    input.disabled = pending;
    joinButton.style.opacity = pending ? '0.7' : '1';
    input.style.opacity = pending ? '0.78' : '1';
    status.textContent = pending ? 'Joining...' : '';
    status.style.color = '#B0B8C8';
  }

  function dispose(): void {
    if (disposed) return;
    disposed = true;
    unsubscribe();
    panel.removeEventListener('submit', handleSubmit);
    cancelButton.removeEventListener('click', handleCancel);
    overlay.remove();
  }

  function handleCancel(): void {
    if (pending) return;
    opts.onCancel?.();
    dispose();
  }

  function handleSubmit(event: SubmitEvent): void {
    event.preventDefault();
    if (pending) return;
    const code = input.value.trim().toUpperCase();
    if (code.length === 0) {
      status.textContent = 'Enter a room code.';
      status.style.color = '#FFB0A0';
      input.focus();
      return;
    }
    setPending(true);
    opts.send({
      type: 'join_room',
      code,
      playerName: opts.playerName,
      ...(opts.preferredCustomization == null
        ? {}
        : { customization: opts.preferredCustomization }),
    });
  }

  const unsubscribe = opts.subscribe((message) => {
    if (disposed) return;
    if (message.type === 'room_joined') {
      const accepted = message as RoomJoinedMessage;
      dispose();
      opts.onAccepted(accepted);
      return;
    }
    if (
      message.type === 'notification' &&
      pending &&
      (message.level === 'error' || message.text === 'maintenance_update_pending')
    ) {
      setPending(false);
      status.textContent = formatJoinError(message.text);
      status.style.color = '#FFB0A0';
      input.focus();
      input.select();
    }
  });

  panel.addEventListener('submit', handleSubmit);
  cancelButton.addEventListener('click', handleCancel);
  opts.mount.appendChild(overlay);
  input.focus();
  input.select();

  return { element: overlay, dispose };
}
